import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { EMPTY, catchError, expand, map, of, reduce } from 'rxjs';
import { DictionaryTerm } from 'src/app/interfaces/dictionary';
import {
  DICTIONARY_PUBLISHED_TERMS_FILTER_BASE,
  GET_DICTIONARY_TERM_BY_SLUG_QUERY,
  GET_DICTIONARY_TERMS_QUERY,
} from 'src/app/services/dictionary.config';
import { SeoService } from 'src/app/services/seo.service';
import {
  applyDictionaryIndexSeo,
  applyDictionaryTermSeo,
  dictionaryTermPath,
} from './dictionary-page-seo.util';

const PAGE_SIZE = 200;

export interface DictionarySsrIndex {
  kind: 'index';
  terms: DictionaryTerm[];
}

export interface DictionarySsrTerm {
  kind: 'term';
  term: DictionaryTerm;
}

export type DictionarySsrData = DictionarySsrIndex | DictionarySsrTerm | null;

interface PublishedTermsPage {
  dictionaryTermsPublished?: {
    items?: DictionaryTerm[];
    hasNextPage?: boolean;
  };
}

/**
 * Loads published glossary copy for the SSR HTML response.
 * In the browser the route activates immediately and the page loads itself.
 */
export const dictionaryPageResolver: ResolveFn<DictionarySsrData> = (route) => {
  if (isPlatformBrowser(inject(PLATFORM_ID))) {
    return of(null);
  }

  const slug = route.paramMap.get('term')?.trim().toLowerCase() ?? '';
  const apollo = inject(Apollo);
  const seoService = inject(SeoService);
  const router = inject(Router);

  if (slug) {
    return apollo
      .query<{ dictionaryTerm: DictionaryTerm | null }>({
        query: GET_DICTIONARY_TERM_BY_SLUG_QUERY,
        variables: { slug },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          const term = result.data?.dictionaryTerm;
          if (!term?.term?.trim()) {
            return null;
          }
          const canonicalSlug = term.slug?.trim().toLowerCase();
          if (canonicalSlug && canonicalSlug !== slug) {
            return new RedirectCommand(router.parseUrl(dictionaryTermPath(canonicalSlug)));
          }
          applyDictionaryTermSeo(seoService, term);
          return { kind: 'term', term } satisfies DictionarySsrTerm;
        }),
        catchError(() => of(null)),
      );
  }

  const loadPage = (skip: number) =>
    apollo.query<PublishedTermsPage>({
      query: GET_DICTIONARY_TERMS_QUERY,
      variables: {
        filter: DICTIONARY_PUBLISHED_TERMS_FILTER_BASE,
        skip,
        take: PAGE_SIZE,
      },
      fetchPolicy: 'network-only',
    });

  return loadPage(0).pipe(
    expand((result, index) => {
      if (!result.data?.dictionaryTermsPublished?.hasNextPage) {
        return EMPTY;
      }
      const nextSkip = (index + 1) * PAGE_SIZE;
      if (nextSkip > 5_000) {
        return EMPTY;
      }
      return loadPage(nextSkip);
    }),
    map((result) => result.data?.dictionaryTermsPublished?.items ?? []),
    reduce((all, items) => all.concat(items), [] as DictionaryTerm[]),
    map((terms) => {
      const published = terms
        .filter((term) => term.slug?.trim() && term.term?.trim())
        .sort((a, b) => a.term.localeCompare(b.term, undefined, { sensitivity: 'base' }));
      applyDictionaryIndexSeo(seoService, published);
      return { kind: 'index', terms: published } satisfies DictionarySsrIndex;
    }),
    catchError(() => of(null)),
  );
};
